import Phaser from "phaser";
import { HOME_CELL_PX } from "@/home/grid";
import {
  PLAYER_DISPLAY_HEIGHT_PX,
  SKILL_BUFF_FX_ALPHA,
  SKILL_DISPLAY_HEIGHT_PX,
} from "@/character/skills/constants";
import { skillAnimationFrameRange } from "@/character/skills/parse-skill-sheet";
import { emitCombatSitRestChanged } from "@/home/home-combat-sit-rest";
import {
  HomeEvents,
  type HomeDataChangedPayload,
  type HomeEditModePayload,
  type HomeProgressionChangedPayload,
  type HomeAdjustPartDepthBiasPayload,
  type HomeAdjustPlacementTransformPayload,
  type HomeSelectPlacementPayload,
  type HomeSkillRuntimePayload,
  type HomeSitRestPayload,
  type HomeUseSkillPayload,
} from "@/home/home-events";
import {
  fitImageContainInFootprint,
  fitImageCoverInBox,
} from "@/game/fit-image-footprint";
import { normalizeImageDataUrl } from "@/home/image-data-url";
import {
  attachPlayerShadowCompanion,
  type PlayerShadowCompanion,
} from "@/game/shadow/player-shadow-companion";
import {
  attachPlayerPetCompanion,
  type PlayerPetCompanion,
} from "@/game/pet/player-pet-companion";
import { PET_DEPTH_BEHIND } from "@/game/pet/pet-constants";
import type { PetDefinition } from "@/pet/types";
import { spawnDefenderShadowProp } from "@/game/shadow/defender-shadow-prop";
import { SHADOW_DEPTH_BEHIND } from "@/shadow/constants";
import {
  fitMonsterSpriteInFootprint,
  partHasMonsterSheet,
  partMonsterAttackAnimKey,
  partMonsterIdleAnimKey,
  partSpritesheetTextureKey,
  registerPartMonsterSheet,
} from "@/home/part-monster-sheet";
import { addSkillSpritesheetTexture } from "@/game/load-data-texture";
import { skillChainGcdMs } from "@/character/skills/skill-chain-gcd";
import {
  canPlaceAt,
  cellHasPlatform,
  effectivePlacementDepthBias,
  effectivePlacementPlaceScale,
  findWalkPath,
  isCellWalkable,
  partById,
  PLATFORM_JUMP_RANGE,
} from "@/home/placement";
import {
  TowerEvents,
  type TowerFocusTargetChangedPayload,
  type TowerSkillUseAcceptedPayload,
  type TowerSkillUseRejectedPayload,
} from "@/tower/tower-events";
import {
  clampPlacementDepthBiasForGrid,
  normalizePartKind,
  placementDepth,
  playerSortDepth,
  type HomePartKind,
} from "@/home/part-kind";
import { socketWorldRects } from "@/home/sockets";
import type { HomePlacement, HomeRoomData } from "@/home/storage";
import {
  createDefaultHomeRoom,
  HOME_DEPTH_BIAS_STEP,
  HOME_PLACEMENT_DEPTH_BIAS_MAX,
  HOME_PLACEMENT_DEPTH_BIAS_MIN,
  normalizePlacementRotation,
  resolveRoomBackdropUrl,
} from "@/home/storage";
import { themeById } from "@/home/themes";
import {
  createInitialProgression,
  type CharacterProgression,
} from "@/game/model/character-progression";
import {
  attackGapMs,
  moveSpeedMultiplier,
  mergeStatBonuses,
  playerRatings,
  type CombatRatings,
  type StatAllocation,
} from "@/game/model/character-stats";
import { attachPlayerRankBadges, RankBadgeOverhead } from "@/rank/rank-badge-overhead";
import { GameEvents } from "@/game/events/game-events";
import { getGameSettings } from "@/game/model/game-settings";
import { resolveHit } from "@/game/model/combat-resolve";
import { monsterCombatFromPart } from "@/game/model/monster-stats";
import {
  combatAttackRange,
  DUMMY_DEFENDER_LEVEL,
  monsterDefaultHp,
  MONSTER_HP_IDLE_RESET_MS,
} from "@/game/data/combat";
import {
  CRIT_TINT_GOLD,
  FloatingCombatTextPool,
} from "@/game/ui/floating-combat-text";
import { playLevelUpEffect, pulseSpriteLevelUp } from "@/game/ui/level-up-effect";
import { SceneKeys } from "@/game/data/scene-keys";

export type PlaceGhostState = {
  partId: string;
  cellsW: number;
  cellsH: number;
  col: number;
  row: number;
  valid: boolean;
};

function playerTextureKey(scene: Phaser.Scene): { key: string; frame?: number } {
  if (scene.anims.exists("player-idle")) {
    return { key: "player-spritesheet", frame: 0 };
  }
  if (scene.textures.exists("player-spritesheet")) {
    return { key: "player-spritesheet", frame: 0 };
  }
  if (scene.textures.exists("player-idle")) {
    return { key: "player-idle" };
  }
  return { key: "player-placeholder" };
}

function applyPlayerScale(sprite: Phaser.GameObjects.Sprite, scene: Phaser.Scene): void {
  const grid = scene.registry.get("playerSpritesheetGrid") as
    | { frameHeight: number }
    | undefined;
  const targetHeight = PLAYER_DISPLAY_HEIGHT_PX;
  const scale = grid ? targetHeight / grid.frameHeight : 2;
  sprite.setScale(scale);
}

function partTextureKey(partId: string): string {
  return `home-part-${partId}`;
}

type PlacementSprite = (Phaser.GameObjects.Image | Phaser.GameObjects.Sprite) & {
  placementId?: string;
};

/** Fixed world margin around the placement grid (world coords, not screen). */
function worldMetrics(homeData: HomeRoomData): {
  worldW: number;
  worldH: number;
  gridOriginX: number;
  gridOriginY: number;
} {
  const gridW = homeData.gridCols * HOME_CELL_PX;
  const gridH = homeData.gridRows * HOME_CELL_PX;
  const marginX = Math.max(200, Math.round(gridW * 0.35));
  const marginTop = Math.max(160, Math.round(gridH * 0.45));
  const marginBottom = Math.max(220, Math.round(gridH * 0.55));
  const worldW = gridW + marginX * 2;
  const worldH = gridH + marginTop + marginBottom;
  return {
    worldW,
    worldH,
    gridOriginX: marginX,
    gridOriginY: marginTop,
  };
}

export class HomeRoomScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private walkTween: Phaser.Tweens.Tween | null = null;
  private walkPathQueue: { col: number; row: number }[] = [];
  private worldInputLocked = false;
  private walkZone!: Phaser.GameObjects.Rectangle;
  private roomGradient!: Phaser.GameObjects.Graphics;
  private shellBg: Phaser.GameObjects.Image | null = null;
  private roomBg: Phaser.GameObjects.Image | null = null;
  private floorSkin: Phaser.GameObjects.Image | null = null;
  private dimRect!: Phaser.GameObjects.Rectangle;
  private floorRect!: Phaser.GameObjects.Rectangle;
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private homeData!: HomeRoomData;
  private designLayout = false;
  private editMode = false;
  private gridOriginX = 0;
  private gridOriginY = 0;
  private worldW = 0;
  private worldH = 0;
  private placementSprites: PlacementSprite[] = [];
  private selectedPlacementId: string | null = null;
  private selectionOutline!: Phaser.GameObjects.Graphics;
  private layerGizmo!: Phaser.GameObjects.Container;
  private layerGizmoUpFill!: Phaser.GameObjects.Rectangle;
  private layerGizmoDownFill!: Phaser.GameObjects.Rectangle;
  private layerGizmoBiasText!: Phaser.GameObjects.Text;
  private layerGizmoRepeatTimer: Phaser.Time.TimerEvent | null = null;
  private ghostGraphics!: Phaser.GameObjects.Graphics;
  private socketGraphics!: Phaser.GameObjects.Graphics;
  private ghostImage: Phaser.GameObjects.Image | null = null;
  private placeGhost: PlaceGhostState | null = null;
  private loadingTextureKeys = new Set<string>();
  private playerGrid = { col: 0, row: 0 };
  private showSockets = false;
  private pendingAttackPlacementId: string | null = null;
  private combatTargetPlacementId: string | null = null;
  private combatActiveBroadcast = false;
  private autoCombatEnabled = false;
  private sitRestEnabled = false;
  private combatStrikeHandler:
    | ((animation: Phaser.Animations.Animation) => void)
    | null = null;
  private monsterHpByPlacementId = new Map<string, number>();
  private monsterLastHitAtByPlacementId = new Map<string, number>();
  private structureCombat: {
    maxHp: number;
    defense: number;
    currentHp: number;
    solidPlacementId: string | null;
    raidMode: boolean;
    allowAttack: boolean;
  } | null = null;
  private structureHpCurrent = 0;
  private structureSessionDamage = 0;
  private structureAssaultEnded = false;
  private progression: CharacterProgression = createInitialProgression();
  private combatFloaters: FloatingCombatTextPool | null = null;
  private activeBuffBonus: Partial<StatAllocation> | null = null;
  private activeBuffUntil = 0;
  private combatGapTimer: Phaser.Time.TimerEvent | null = null;
  private skillRuntime: HomeSkillRuntimePayload = {
    unlockedSlots: 3,
    cooldownMs: 30_000,
    slots: [],
    skills: [],
  };
  private loadingSkillTextureKeys = new Set<string>();
  private skillFxSprite: Phaser.GameObjects.Sprite | null = null;
  private buffStopTimer: Phaser.Time.TimerEvent | null = null;
  private rankBadges: RankBadgeOverhead | null = null;
  private playerShadow: PlayerShadowCompanion | null = null;
  private playerPet: PlayerPetCompanion | null = null;
  private defenderShadowProp: Phaser.GameObjects.Sprite | null = null;

  private onDataChanged = (event: Event) => {
    const detail = (event as CustomEvent<HomeDataChangedPayload>).detail;
    const prevCols = this.homeData.gridCols;
    const prevRows = this.homeData.gridRows;
    this.homeData = detail.data;
    const sizeChanged =
      prevCols !== this.homeData.gridCols || prevRows !== this.homeData.gridRows;

    if (sizeChanged) {
      this.applyWorldMetrics();
      this.playerGrid.col = Math.min(
        Math.max(0, this.playerGrid.col),
        Math.max(0, this.homeData.gridCols - 1),
      );
      this.playerGrid.row = Math.min(
        Math.max(0, this.playerGrid.row),
        Math.max(0, this.homeData.gridRows - 1),
      );
      this.setupCamera();
      if (this.editMode || this.designLayout) this.centerCameraOnGrid();
    }

    this.syncPartTextures();
    this.syncStageShell();
    this.syncRoomBackdrop();
    this.syncFloorSkin();
    if (sizeChanged) {
      this.layoutWorldGraphics();
      this.drawGrid();
    }
    this.syncPlacements();
    if (this.placeGhost) {
      this.setPlaceGhost(this.placeGhost);
    }
  };

  private onEditModeChanged = (event: Event) => {
    const detail = (event as CustomEvent<HomeEditModePayload>).detail;
    this.setEditMode(detail.enabled);
  };

  private boundDrag = (
    _pointer: Phaser.Input.Pointer,
    gameObject: Phaser.GameObjects.GameObject,
    dragX: number,
    dragY: number,
  ) => {
    const sprite = gameObject as PlacementSprite;
    sprite.x = dragX;
    sprite.y = dragY;
    const placementId = sprite.placementId;
    if (!placementId) return;
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return;
    const part = partById(this.homeData, placement.partId);
    if (!part) return;
    const snap = this.spriteToGridTopLeftRaw(sprite, part);
    const valid = canPlaceAt(
      this.homeData,
      placement.partId,
      snap.col,
      snap.row,
      placementId,
    );
    this.setPlaceGhost({
      partId: placement.partId,
      cellsW: part.cellsW,
      cellsH: part.cellsH,
      col: snap.col,
      row: snap.row,
      valid,
    });
  };

  private boundDragEnd = (
    _pointer: Phaser.Input.Pointer,
    gameObject: Phaser.GameObjects.GameObject,
  ) => {
    const sprite = gameObject as PlacementSprite;
    const placementId = sprite.placementId;
    this.clearPlaceGhost();
    if (!placementId) return;
    const topLeft = this.spriteToGridTopLeft(sprite, placementId);
    if (!topLeft) {
      this.syncPlacements();
      return;
    }
    window.dispatchEvent(
      new CustomEvent(HomeEvents.MovePlacement, {
        detail: { placementId, gridX: topLeft.col, gridY: topLeft.row },
      }),
    );
  };

  constructor() {
    super(SceneKeys.HomeRoom);
  }

  create(): void {
    this.homeData =
      (this.registry.get("homeRoomData") as HomeRoomData | undefined) ??
      createDefaultHomeRoom();

    const structureRaw = this.registry.get("homeStructureCombat");
    if (structureRaw && typeof structureRaw === "object") {
      const sc = structureRaw as {
        maxHp: number;
        defense: number;
        currentHp: number;
        solidPlacementId: string | null;
        raidMode: boolean;
        allowAttack: boolean;
      };
      this.structureCombat = sc;
      this.structureHpCurrent = Math.max(0, Math.floor(sc.currentHp));
    }

    this.applyWorldMetrics();

    this.roomGradient = this.add.graphics().setDepth(-10);
    this.dimRect = this.add.rectangle(0, 0, 1, 1, 0x0c121c, 0.1).setDepth(-1);
    this.floorRect = this.add
      .rectangle(0, 0, 1, 1, 0x1e2836, 0.16)
      .setStrokeStyle(2, 0x6ec8ff, 0.35)
      .setDepth(0);

    this.gridGraphics = this.add.graphics().setDepth(1);
    this.selectionOutline = this.add.graphics().setDepth(20);
    this.layerGizmo = this.buildLayerGizmo();
    this.ghostGraphics = this.add.graphics().setDepth(21);
    this.socketGraphics = this.add.graphics().setDepth(19);

    const texture = playerTextureKey(this);
    this.playerGrid = {
      col: Math.floor(this.homeData.gridCols / 2),
      row: this.homeData.gridRows - 1,
    };

    this.player = this.add.sprite(0, 0, texture.key, texture.frame);
    this.player.setOrigin(0.5, 1);
    applyPlayerScale(this.player, this);
    this.refreshPlayerDepth();

    if (this.anims.exists("player-idle")) {
      this.player.play("player-idle");
    }
    this.playerShadow = attachPlayerShadowCompanion(this, this.player);

    const equippedPetDef = this.registry.get("equippedPet") as PetDefinition | undefined;
    if (equippedPetDef) {
      this.playerPet = attachPlayerPetCompanion(this, this.player, {
        progression: this.progression,
        pet: equippedPetDef,
        skillRuntime: this.registry.get("petSkillRuntime") as
          | import("@/home/home-events").HomeSkillRuntimePayload
          | undefined,
        battleActive: () => this.autoCombatEnabled,
        resolveDisplayDepth: (pet, player) => {
          const localY = pet.y - this.gridOriginY;
          const row = Math.max(
            0,
            Math.min(this.homeData.gridRows - 1, Math.floor(localY / HOME_CELL_PX)),
          );
          const rowDepth = playerSortDepth(row);
          return Math.min(player.depth - PET_DEPTH_BEHIND, rowDepth - 0.05);
        },
      });
    }

    this.walkZone = this.add
      .rectangle(0, 0, 1, 1, 0x000000, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(2);

    this.walkZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (this.editMode) return;
      this.pendingAttackPlacementId = null;
      this.stopCombatLoop();
      this.movePlayerTo(pointer.worldX, pointer.worldY);
    });

    this.layoutWorldGraphics();
    this.syncDefenderShadowProp();
    this.setupCamera();

    this.syncPartTextures();
    this.syncStageShell();
    this.syncRoomBackdrop();
    this.syncFloorSkin();
    this.designLayout = Boolean(this.registry.get("homeRoomDesignLayout"));
    this.drawGrid();
    this.syncPlacements();
    if (this.designLayout) {
      this.setEditMode(true);
    } else {
      this.setEditMode(false);
    }

    const runtime = this.registry.get("homeSkillRuntime") as HomeSkillRuntimePayload | undefined;
    if (runtime) {
      this.skillRuntime = runtime;
    }
    const prog = this.registry.get("characterProgression") as CharacterProgression | undefined;
    if (prog) {
      this.progression = prog;
    }
    this.combatFloaters = new FloatingCombatTextPool(this);
    this.registerAllSkillAnims();

    void attachPlayerRankBadges(this, this.player)
      .then((o) => {
        this.rankBadges = o;
      })
      .catch(() => {
        this.rankBadges = null;
      });

    this.scale.on("resize", this.onViewportResize, this);

    window.addEventListener(HomeEvents.DataChanged, this.onDataChanged);
    window.addEventListener(HomeEvents.EditModeChanged, this.onEditModeChanged);
    window.addEventListener(HomeEvents.SkillRuntimeChanged, this.onSkillRuntimeChanged);
    window.addEventListener(HomeEvents.UseSkill, this.onUseSkill);
    window.addEventListener(TowerEvents.AutoChaseChanged, this.onAutoCombatChanged);
    window.addEventListener(HomeEvents.SitRestChanged, this.onSitRestChanged);
    window.addEventListener(HomeEvents.ProgressionChanged, this.onProgressionChanged);
    window.addEventListener(HomeEvents.EquipmentChanged, this.onEquipmentChanged);
    window.addEventListener(GameEvents.GameSettingsChanged, this.onGameSettingsChanged);

    window.dispatchEvent(new CustomEvent(HomeEvents.RoomSceneReady));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off("resize", this.onViewportResize, this);
      window.removeEventListener(HomeEvents.DataChanged, this.onDataChanged);
      window.removeEventListener(HomeEvents.EditModeChanged, this.onEditModeChanged);
      window.removeEventListener(HomeEvents.SkillRuntimeChanged, this.onSkillRuntimeChanged);
      window.removeEventListener(HomeEvents.UseSkill, this.onUseSkill);
      window.removeEventListener(TowerEvents.AutoChaseChanged, this.onAutoCombatChanged);
      window.removeEventListener(HomeEvents.SitRestChanged, this.onSitRestChanged);
      window.removeEventListener(HomeEvents.ProgressionChanged, this.onProgressionChanged);
      window.removeEventListener(HomeEvents.EquipmentChanged, this.onEquipmentChanged);
      window.removeEventListener(GameEvents.GameSettingsChanged, this.onGameSettingsChanged);
      this.combatGapTimer?.remove(false);
      this.combatFloaters?.destroy();
      this.combatFloaters = null;
      this.rankBadges?.destroy();
      this.rankBadges = null;
    });
  }

  private onProgressionChanged = (event: Event) => {
    const detail = (event as CustomEvent<HomeProgressionChangedPayload>).detail;
    const levelsGained = detail.levelsGained ?? 0;
    this.progression = detail.progression;
    if (levelsGained <= 0 || this.editMode) return;
    if (!this.player?.active || !this.player.visible) return;
    const topY = this.player.y - this.player.displayHeight;
    playLevelUpEffect(this, this.player.x, topY, detail.progression.level);
    pulseSpriteLevelUp(this, this.player);
  };

  private onEquipmentChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ bonus: Partial<StatAllocation> | undefined }>).detail;
    this.registry.set("equipmentStatBonus", detail.bonus);
  };

  private onGameSettingsChanged = () => {
    this.applyPlayerAttackAnimFps(getGameSettings().playerAttackAnimFps);
  };

  private applyPlayerAttackAnimFps(fps: number): void {
    const anim = this.anims.get("player-attack");
    if (anim) anim.frameRate = fps;
  }

  private onSkillRuntimeChanged = (event: Event) => {
    this.skillRuntime = (event as CustomEvent<HomeSkillRuntimePayload>).detail;
    this.registerAllSkillAnims();
  };

  private onUseSkill = (event: Event) => {
    if (this.editMode) return;
    if (this.sitRestEnabled) return;
    const { skillId, slotIndex } = (event as CustomEvent<HomeUseSkillPayload>).detail;
    this.playSkillMotion(skillId, slotIndex);
  };

  private onAutoCombatChanged = (event: Event): void => {
    const { enabled } = (event as CustomEvent<{ enabled: boolean }>).detail;
    this.autoCombatEnabled = enabled === true;
    if (!enabled) return;
    this.tickHomeAutoCombat();
  };

  private onSitRestChanged = (event: Event): void => {
    const { enabled } = (event as CustomEvent<HomeSitRestPayload>).detail;
    this.sitRestEnabled = enabled === true;
    if (this.sitRestEnabled) {
      this.pendingAttackPlacementId = null;
      this.clearWalkPath();
      this.stopCombatLoop();
      this.applySitRestPose();
      return;
    }
    this.clearSitRestPose();
  };

  private applySitRestPose(): void {
    if (this.editMode) return;
    if (this.anims.exists("player-sit")) {
      this.player.play("player-sit", true);
      return;
    }
    if (this.anims.exists("player-idle")) {
      this.player.play("player-idle", true);
    }
  }

  private clearSitRestPose(): void {
    if (this.walkTween) return;
    if (this.combatTargetPlacementId) return;
    if (this.anims.exists("player-idle")) {
      this.player.play("player-idle", true);
    }
  }

  private emitSkillUseAccepted(slotIndex: number): void {
    const ratings = this.playerCombatRatings();
    window.dispatchEvent(
      new CustomEvent(TowerEvents.SkillUseAccepted, {
        detail: {
          slotIndex,
          chainGcdMs: skillChainGcdMs(ratings.level, ratings.attackSpeed),
        } satisfies TowerSkillUseAcceptedPayload,
      }),
    );
  }

  private emitSkillUseRejected(slotIndex: number): void {
    window.dispatchEvent(
      new CustomEvent(TowerEvents.SkillUseRejected, {
        detail: { slotIndex } satisfies TowerSkillUseRejectedPayload,
      }),
    );
  }

  private skillTextureKey(skillId: string): string {
    return `home-skill-${skillId}`;
  }

  private skillAnimKey(skillId: string): string {
    return `skill-${skillId}`;
  }

  private registerAllSkillAnims(): void {
    for (const skill of this.skillRuntime.skills) {
      this.ensureSkillAnim(skill, false);
      this.ensureSkillAnim(skill, true);
    }
  }

  private skillTextureHasFrames(texKey: string, frameCount: number): boolean {
    if (!this.textures.exists(texKey)) return false;
    return this.textures.get(texKey).frameTotal >= frameCount;
  }

  private removeSkillTextureAndAnims(skillId: string): void {
    const texKey = this.skillTextureKey(skillId);
    const base = this.skillAnimKey(skillId);
    for (const animKey of [base, `${base}-loop`]) {
      if (this.anims.exists(animKey)) this.anims.remove(animKey);
    }
    if (this.textures.exists(texKey)) this.textures.remove(texKey);
  }

  private registerSkillAnim(
    skill: HomeSkillRuntimePayload["skills"][number],
    loop: boolean,
  ): void {
    const texKey = this.skillTextureKey(skill.id);
    const animKey = loop ? `${this.skillAnimKey(skill.id)}-loop` : this.skillAnimKey(skill.id);
    if (this.anims.exists(animKey)) return;
    if (!this.skillTextureHasFrames(texKey, skill.grid.columns)) return;
    const { start, end } = skillAnimationFrameRange(skill.grid);
    this.anims.create({
      key: animKey,
      frames: this.anims.generateFrameNumbers(texKey, { start, end }),
      frameRate: skill.frameRate,
      repeat: loop ? -1 : 0,
    });
  }

  private ensureSkillAnim(
    skill: HomeSkillRuntimePayload["skills"][number],
    loop: boolean,
  ): string {
    const texKey = this.skillTextureKey(skill.id);
    const animKey = loop ? `${this.skillAnimKey(skill.id)}-loop` : this.skillAnimKey(skill.id);
    const frameCount = skill.grid.columns;

    if (this.skillTextureHasFrames(texKey, frameCount)) {
      this.registerSkillAnim(skill, loop);
      return animKey;
    }

    if (this.loadingSkillTextureKeys.has(texKey)) return animKey;
    this.loadingSkillTextureKeys.add(texKey);
    this.removeSkillTextureAndAnims(skill.id);

    const onAdd = (addedKey: string) => {
      if (addedKey !== texKey) return;
      this.textures.off(Phaser.Textures.Events.ADD, onAdd);
      this.loadingSkillTextureKeys.delete(texKey);
      this.registerSkillAnim(skill, false);
      this.registerSkillAnim(skill, true);
    };
    this.textures.on(Phaser.Textures.Events.ADD, onAdd);

    void addSkillSpritesheetTexture(
      this.textures,
      texKey,
      normalizeImageDataUrl(skill.spritesheetDataUrl),
      skill.grid.frameWidth,
      skill.grid.frameHeight,
    ).catch(() => {
      this.textures.off(Phaser.Textures.Events.ADD, onAdd);
      this.loadingSkillTextureKeys.delete(texKey);
    });

    return animKey;
  }

  private applySkillFxScale(sprite: Phaser.GameObjects.Sprite, skill: HomeSkillRuntimePayload["skills"][number]): void {
    let scale = SKILL_DISPLAY_HEIGHT_PX / skill.grid.frameHeight;
    if (skill.attackPattern === "aoe") {
      scale *= skill.aoeRangeMultiplier ?? 1;
    }
    sprite.setScale(scale);
  }

  private clearSkillFx(): void {
    this.buffStopTimer?.remove(false);
    this.buffStopTimer = null;
    if (this.skillFxSprite) {
      this.skillFxSprite.clearTint();
      this.skillFxSprite.destroy();
    }
    this.skillFxSprite = null;
    this.activeBuffBonus = null;
    this.activeBuffUntil = 0;
  }

  private syncSkillFxDepth(): void {
    if (this.skillFxSprite) {
      this.skillFxSprite.setDepth(this.player.depth + 1);
    }
  }

  private playSkillFxAtPlayer(
    skill: HomeSkillRuntimePayload["skills"][number],
    loop: boolean,
    onDone?: () => void,
  ): void {
    const texKey = this.skillTextureKey(skill.id);
    const animKey = this.ensureSkillAnim(skill, loop);
    const startFx = () => {
      if (!this.textures.exists(texKey)) return;
      this.clearSkillFx();
      this.skillFxSprite = this.add.sprite(this.player.x, this.player.y, texKey, 0);
      this.skillFxSprite.setOrigin(0.5, 1);
      this.applySkillFxScale(this.skillFxSprite, skill);
      this.syncSkillFxDepth();
      this.skillFxSprite.setFlipX(this.player.flipX);
      this.skillFxSprite.setAlpha(loop ? SKILL_BUFF_FX_ALPHA : 1);
      if (!this.anims.exists(animKey)) return;
      this.skillFxSprite.play(animKey);
      if (loop) return;
      this.skillFxSprite.once("animationcomplete", (animation: Phaser.Animations.Animation) => {
        if (animation.key !== animKey) return;
        this.clearSkillFx();
        onDone?.();
      });
    };
    if (this.textures.exists(texKey) && this.anims.exists(animKey)) {
      startFx();
      return;
    }
    const onAdd = (addedKey: string) => {
      if (addedKey !== texKey) return;
      this.textures.off(Phaser.Textures.Events.ADD, onAdd);
      if (this.anims.exists(animKey)) startFx();
    };
    this.textures.on(Phaser.Textures.Events.ADD, onAdd);
    this.ensureSkillAnim(skill, loop);
  }

  private playSkillMotion(skillId: string, slotIndex: number): void {
    const skill = this.skillRuntime.skills.find((s) => s.id === skillId);
    if (!skill) {
      this.emitSkillUseRejected(slotIndex);
      return;
    }
    this.emitSkillUseAccepted(slotIndex);
    const savedCombatTarget = this.combatTargetPlacementId;
    this.clearCombatStrikeHandler();
    this.clearWalkPath();
    this.refreshPlayerDepth();

    if (skill.kind === "buff") {
      const duration = Math.max(500, skill.durationMs);
      this.activeBuffBonus = skill.buffStatBonus ?? null;
      this.activeBuffUntil = Date.now() + duration;
      this.playSkillFxAtPlayer(skill, true);
      this.buffStopTimer?.remove(false);
      this.buffStopTimer = this.time.delayedCall(duration, () => {
        this.clearSkillFx();
        this.resumeCombatAfterSkill(savedCombatTarget);
      });
      if (this.anims.exists("player-idle")) {
        this.player.play("player-idle", true);
      }
      return;
    }

    let attackSkillFinished = false;
    const finishAttackSkill = () => {
      if (attackSkillFinished) return;
      attackSkillFinished = true;
      this.resumeCombatAfterSkill(savedCombatTarget);
    };

    if (this.anims.exists("player-attack")) {
      this.player.play("player-attack");
      this.player.once("animationcomplete", (animation: Phaser.Animations.Animation) => {
        if (animation.key !== "player-attack") return;
        finishAttackSkill();
      });
    } else {
      finishAttackSkill();
    }
    this.playSkillFxAtPlayer(skill, false, finishAttackSkill);
    const mult = skill.damageMultiplier ?? 1.2;
    if (skill.attackPattern === "aoe") {
      const aoeMult = skill.aoeRangeMultiplier ?? 1;
      for (const placementId of this.monsterPlacementsInAoeAroundPlayer(aoeMult)) {
        this.resolveStrikeOnTarget(placementId, mult);
      }
    } else if (savedCombatTarget) {
      this.resolveStrikeOnTarget(savedCombatTarget, mult);
    }
  }

  update(): void {
    if (this.skillFxSprite?.active) {
      this.skillFxSprite.setPosition(this.player.x, this.player.y);
      this.skillFxSprite.setFlipX(this.player.flipX);
      this.syncSkillFxDepth();
    }
    this.rankBadges?.sync();
    this.playerShadow?.syncFromPlayer(this.player);
    this.playerPet?.tick(16, this.player);
    this.tickHomeAutoCombat();
  }

  private tickHomeAutoCombat(): void {
    if (!this.autoCombatEnabled || this.editMode || this.sitRestEnabled) return;
    const activeId = this.combatTargetPlacementId;
    if (activeId && this.canContinueCombat(activeId)) return;
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const placement of this.homeData.placements) {
      const kind = this.combatTargetKind(placement.id);
      if (kind !== "monster" && kind !== "attack_dummy") continue;
      if (kind === "monster" && this.monsterHp(placement.id) <= 0) continue;
      const dist = this.manhattanToPlacement(placement.id);
      if (dist === null) continue;
      if (dist < bestDist) {
        bestDist = dist;
        bestId = placement.id;
      }
    }
    if (bestId) {
      this.tryEngageCombatTarget(bestId);
    }
  }

  private applyWorldMetrics(): void {
    const m = worldMetrics(this.homeData);
    this.worldW = m.worldW;
    this.worldH = m.worldH;
    this.gridOriginX = m.gridOriginX;
    this.gridOriginY = m.gridOriginY;
  }

  private setupCamera(): void {
    const cam = this.cameras.main;
    cam.setBounds(0, 0, this.worldW, this.worldH);
    cam.setZoom(1);
    cam.roundPixels = true;
    cam.startFollow(this.player, true, 0.12, 0.12);
    cam.setDeadzone(48, 64);
  }

  /** Viewport (canvas) may resize; world size stays fixed. */
  private onViewportResize = (): void => {
    this.cameras.main.setBounds(0, 0, this.worldW, this.worldH);
    if (this.editMode || this.designLayout) {
      this.centerCameraOnGrid();
    }
    this.drawGrid();
    this.syncPlacements();
  };

  private gridCenterWorld(): { x: number; y: number } {
    const gridW = this.homeData.gridCols * HOME_CELL_PX;
    const gridH = this.homeData.gridRows * HOME_CELL_PX;
    return {
      x: this.gridOriginX + gridW / 2,
      y: this.gridOriginY + gridH / 2,
    };
  }

  private layoutEditorZoom(): number {
    if (!this.designLayout) return 1;
    const z = this.registry.get("homeRoomEditorZoom");
    return typeof z === "number" && z > 0 ? z : 1;
  }

  private applyLayoutCameraZoom(): void {
    this.cameras.main.setZoom(this.layoutEditorZoom());
  }

  private centerCameraOnGrid(): void {
    const cam = this.cameras.main;
    cam.stopFollow();
    this.applyLayoutCameraZoom();
    const center = this.gridCenterWorld();
    cam.centerOn(center.x, center.y);
  }

  private hasStageShell(): boolean {
    return Boolean(this.homeData.shellPartId);
  }

  private layoutWorldGraphics(): void {
    const gridW = this.homeData.gridCols * HOME_CELL_PX;
    const gridH = this.homeData.gridRows * HOME_CELL_PX;
    const shellFramed = this.hasStageShell() && Boolean(this.shellBg?.visible);

    if (this.shellBg?.visible) {
      this.shellBg.setPosition(this.worldW / 2, this.worldH / 2);
      fitImageCoverInBox(this.shellBg, this.worldW, this.worldH);
    }

    if (this.roomBg?.visible) {
      if (shellFramed) {
        this.roomBg.setPosition(this.gridOriginX + gridW / 2, this.gridOriginY + gridH / 2);
        fitImageCoverInBox(this.roomBg, gridW, gridH);
      } else {
        this.roomBg.setPosition(this.worldW / 2, this.worldH / 2);
        fitImageCoverInBox(this.roomBg, this.worldW, this.worldH);
      }
    }

    if (shellFramed) {
      this.dimRect
        .setPosition(this.gridOriginX + gridW / 2, this.gridOriginY + gridH / 2)
        .setSize(gridW, gridH);
    } else {
      this.dimRect.setPosition(this.worldW / 2, this.worldH / 2).setSize(this.worldW, this.worldH);
    }
    this.floorRect
      .setPosition(this.gridOriginX + gridW / 2, this.gridOriginY + gridH / 2 + 12)
      .setSize(gridW + 16, gridH + 24);
    if (this.floorSkin) {
      this.floorSkin.setPosition(this.gridOriginX + gridW / 2, this.gridOriginY + gridH / 2);
      fitImageCoverInBox(this.floorSkin, gridW, gridH);
    }
    this.walkZone
      .setPosition(this.gridOriginX + gridW / 2, this.gridOriginY + gridH / 2)
      .setSize(gridW, gridH);

    this.player.setPosition(
      this.gridCellCenterX(this.playerGrid.col),
      this.gridCellBottomY(this.playerGrid.row),
    );
    this.refreshPlayerDepth();
  }

  private paintThemeGradient(): void {
    const theme = themeById(this.homeData.themeId);
    const shellFramed = this.hasStageShell();
    const gridW = this.homeData.gridCols * HOME_CELL_PX;
    const gridH = this.homeData.gridRows * HOME_CELL_PX;
    const fillX = shellFramed ? this.gridOriginX : 0;
    const fillY = shellFramed ? this.gridOriginY : 0;
    const fillW = shellFramed ? gridW : this.worldW;
    const fillH = shellFramed ? gridH : this.worldH;

    this.roomGradient.clear();
    this.roomGradient.setVisible(true);
    this.roomGradient.fillGradientStyle(
      theme.gradientTop,
      theme.gradientTop,
      theme.gradientBottom,
      theme.gradientBottom,
      1,
    );
    this.roomGradient.fillRect(fillX, fillY, fillW, fillH);

    const midY = fillY + fillH * 0.35;
    const midH = fillH * 0.4;
    this.roomGradient.fillStyle(theme.gradientMid, theme.midAlpha);
    this.roomGradient.fillRect(fillX, midY, fillW, midH);

    this.dimRect.setFillStyle(0x0c121c, theme.dimAlpha);
    if (!this.homeData.floorPartId) {
      this.floorRect.setFillStyle(theme.floorTint, 0.22);
    }
  }

  private syncStageShell(): void {
    const partId = this.homeData.shellPartId;
    if (!partId) {
      if (this.shellBg) this.shellBg.setVisible(false);
      this.layoutWorldGraphics();
      return;
    }

    const key = partTextureKey(partId);
    if (!this.textures.exists(key)) {
      this.ensurePartTexture(partId);
      return;
    }

    if (!this.shellBg) {
      this.shellBg = this.add.image(0, 0, key).setDepth(-100);
    } else {
      this.shellBg.setTexture(key);
    }
    this.shellBg.setVisible(true).setDepth(-100);
    this.layoutWorldGraphics();
  }

  private syncRoomBackdrop(): void {
    const partId = this.homeData.backdropPartId;
    if (partId) {
      const key = partTextureKey(partId);
      if (!this.textures.exists(key)) {
        this.ensurePartTexture(partId);
        return;
      }
      this.roomGradient.clear();
      this.roomGradient.setVisible(false);
      if (!this.roomBg) {
        this.roomBg = this.add.image(0, 0, key).setDepth(-10);
      } else {
        this.roomBg.setTexture(key);
      }
      this.roomBg.setVisible(true).setDepth(-10);
      this.dimRect.setFillStyle(0x0c121c, 0.18);
      this.layoutWorldGraphics();
      return;
    }

    const customUrl = resolveRoomBackdropUrl(this.homeData);
    if (customUrl) {
      const key = "home-room-bg-custom";
      if (!this.textures.exists(key)) {
        if (!this.loadingTextureKeys.has(key)) {
          this.loadingTextureKeys.add(key);
          const onAdd = (addedKey: string) => {
            if (addedKey !== key) return;
            this.textures.off(Phaser.Textures.Events.ADD, onAdd);
            this.loadingTextureKeys.delete(key);
            this.syncRoomBackdrop();
          };
          this.textures.on(Phaser.Textures.Events.ADD, onAdd);
          this.textures.addBase64(key, normalizeImageDataUrl(customUrl));
        }
        return;
      }
      this.roomGradient.clear();
      this.roomGradient.setVisible(false);
      if (!this.roomBg) {
        this.roomBg = this.add.image(0, 0, key).setDepth(-10);
      } else {
        this.roomBg.setTexture(key);
      }
      this.roomBg.setVisible(true).setDepth(-10);
      this.dimRect.setFillStyle(0x0c121c, 0.18);
      this.layoutWorldGraphics();
      return;
    }

    if (this.roomBg) this.roomBg.setVisible(false);
    this.paintThemeGradient();
    this.layoutWorldGraphics();
  }

  setShowSockets(show: boolean): void {
    this.showSockets = show;
    this.drawSockets();
  }

  private drawSockets(): void {
    this.socketGraphics.clear();
    if (!this.editMode || !this.showSockets) return;
    this.socketGraphics.lineStyle(1.5, 0xffd27a, 0.9);
    this.socketGraphics.fillStyle(0xffd27a, 0.18);
    for (const rect of socketWorldRects(this.homeData)) {
      const x = this.gridOriginX + rect.x * HOME_CELL_PX;
      const y = this.gridOriginY + rect.y * HOME_CELL_PX;
      const w = rect.w * HOME_CELL_PX;
      const h = rect.h * HOME_CELL_PX;
      this.socketGraphics.fillRect(x + 6, y + 6, w - 12, h - 12);
      this.socketGraphics.strokeRect(x + 6, y + 6, w - 12, h - 12);
    }
  }

  private syncFloorSkin(): void {
    const floorId = this.homeData.floorPartId;
    if (!floorId) {
      this.floorSkin?.destroy();
      this.floorSkin = null;
      const theme = themeById(this.homeData.themeId);
      this.floorRect.setFillStyle(theme.floorTint, 0.22);
      this.floorRect.setAlpha(1);
      return;
    }
    const key = partTextureKey(floorId);
    if (!this.textures.exists(key)) {
      this.ensurePartTexture(floorId);
      return;
    }
    if (!this.floorSkin) {
      this.floorSkin = this.add.image(0, 0, key).setDepth(0.5);
    } else {
      this.floorSkin.setTexture(key);
    }
    this.floorSkin.setAlpha(0.95);
    this.floorRect.setFillStyle(0x1e2836, 1);
    this.floorRect.setAlpha(0.15);
    this.layoutWorldGraphics();
  }

  /** Map browser client coords to grid cell (for HTML drag-drop). */
  pickGridFromClient(clientX: number, clientY: number): { col: number; row: number } | null {
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const cam = this.cameras.main;
    const vx = ((clientX - rect.left) / rect.width) * cam.width;
    const vy = ((clientY - rect.top) / rect.height) * cam.height;
    const world = cam.getWorldPoint(vx, vy);
    return this.worldToGrid(world.x, world.y);
  }

  getSelectedPlacementId(): string | null {
    return this.selectedPlacementId;
  }

  clearSelectedPlacement(): void {
    this.setSelectedPlacement(null);
  }

  selectPlacement(placementId: string | null): void {
    this.setSelectedPlacement(placementId);
  }

  private setSelectedPlacement(placementId: string | null): void {
    this.selectedPlacementId = placementId;
    this.drawSelectionOutline();
    const placement = placementId
      ? this.homeData.placements.find((p) => p.id === placementId)
      : undefined;
    const detail: HomeSelectPlacementPayload = {
      placementId,
      partId: placement?.partId ?? null,
    };
    window.dispatchEvent(
      new CustomEvent(HomeEvents.SelectPlacement, { detail }),
    );
  }

  /** Block walk / pointer input while HTML overlays are open (modals, tower, etc.). */
  setWorldInputEnabled(enabled: boolean): void {
    if (!this.walkZone || !this.input) return;
    const locked = !enabled;
    if (this.worldInputLocked === locked) return;
    this.worldInputLocked = locked;
    if (locked) {
      this.clearWalkPath();
      this.walkZone.disableInteractive();
      this.input.enabled = false;
      return;
    }
    this.input.enabled = true;
    this.applyWalkZoneInteractive();
  }

  private applyWalkZoneInteractive(): void {
    if (!this.walkZone) return;
    const layoutEdit = this.editMode || this.designLayout;
    this.walkZone.disableInteractive();
    if (!layoutEdit && !this.worldInputLocked) {
      this.walkZone.setInteractive({ useHandCursor: true });
    }
  }

  setEditMode(enabled: boolean): void {
    this.editMode = enabled;
    if (!enabled) this.stopLayerGizmoRepeat();
    const layoutEdit = enabled || this.designLayout;
    const cam = this.cameras.main;
    if (layoutEdit) {
      this.stopCombatLoop();
      this.pendingAttackPlacementId = null;
      this.clearWalkPath();
      this.player.setVisible(false);
      this.player.anims?.stop();
      this.centerCameraOnGrid();
    } else {
      cam.setZoom(1);
      this.player.setVisible(true);
      if (this.anims.exists("player-idle")) {
        this.player.play("player-idle", true);
      }
      cam.startFollow(this.player, true, 0.12, 0.12);
      this.setSelectedPlacement(null);
      this.clearPlaceGhost();
      this.showSockets = false;
    }
    this.drawGrid();
    this.syncPlacements();
    this.applyWalkZoneInteractive();
  }

  private syncPartTextures(): void {
    for (const part of this.homeData.parts) {
      this.ensurePartTexture(part.id);
    }
  }

  private ensurePartTexture(partId: string): boolean {
    const flatReady = this.ensurePartFlatTexture(partId);
    const part = partById(this.homeData, partId);
    if (part && partHasMonsterSheet(part)) {
      this.ensurePartSheetTexture(partId);
    }
    return flatReady;
  }

  private ensurePartFlatTexture(partId: string): boolean {
    const key = partTextureKey(partId);
    if (this.textures.exists(key)) return true;
    if (this.loadingTextureKeys.has(key)) return false;

    const part = partById(this.homeData, partId);
    if (!part?.imageDataUrl) return false;

    this.loadingTextureKeys.add(key);
    const dataUrl = normalizeImageDataUrl(part.imageDataUrl);
    const onAdd = (addedKey: string) => {
      if (addedKey !== key) return;
      this.textures.off(Phaser.Textures.Events.ADD, onAdd);
      this.loadingTextureKeys.delete(key);
      this.syncPlacements();
      if (this.homeData.shellPartId === partId) {
        this.syncStageShell();
      }
      if (this.homeData.backdropPartId === partId) {
        this.syncRoomBackdrop();
      }
      if (this.placeGhost?.partId === partId) {
        this.drawPlaceGhost();
      }
    };
    this.textures.on(Phaser.Textures.Events.ADD, onAdd);
    this.textures.addBase64(key, dataUrl);
    return false;
  }

  private ensurePartSheetTexture(partId: string): void {
    const key = partSpritesheetTextureKey(partId);
    if (this.textures.exists(key)) return;
    if (this.loadingTextureKeys.has(key)) return;
    const part = partById(this.homeData, partId);
    if (!part || !partHasMonsterSheet(part)) return;

    this.loadingTextureKeys.add(key);
    void registerPartMonsterSheet(this, part).then(() => {
      this.loadingTextureKeys.delete(key);
      this.syncPlacements();
      if (this.placeGhost?.partId === partId) {
        this.drawPlaceGhost();
      }
    });
  }

  /** Ghost footprint while placing / moving (dashed grid frame). */
  setPlaceGhost(state: PlaceGhostState | null): void {
    this.placeGhost = state;
    this.drawPlaceGhost();
  }

  clearPlaceGhost(): void {
    this.placeGhost = null;
    this.drawPlaceGhost();
  }

  private drawPlaceGhost(): void {
    this.ghostGraphics.clear();
    if (this.ghostImage) {
      this.ghostImage.destroy();
      this.ghostImage = null;
    }
    if (!this.editMode || !this.placeGhost) return;

    const { partId, cellsW, cellsH, col, row, valid } = this.placeGhost;
    const w = cellsW * HOME_CELL_PX;
    const h = cellsH * HOME_CELL_PX;
    const x = this.gridOriginX + col * HOME_CELL_PX;
    const y = this.gridOriginY + row * HOME_CELL_PX;
    const color = valid ? 0x6ec8ff : 0xff6b6b;

    this.ghostGraphics.fillStyle(color, valid ? 0.14 : 0.18);
    this.ghostGraphics.fillRect(x, y, w, h);
    this.drawDashedRect(this.ghostGraphics, x, y, w, h, color, 1, 8, 5);

    const key = partTextureKey(partId);
    if (this.textures.exists(key)) {
      this.ghostImage = this.add.image(x + w / 2, y + h, key);
      const ghostPart = partById(this.homeData, partId);
      const ghostScale = ghostPart?.placeScale ?? 1;
      fitImageContainInFootprint(this.ghostImage, w, h, 0.5, 1, ghostScale);
      this.ghostImage.setAlpha(valid ? 0.55 : 0.35);
      this.ghostImage.setDepth(22);
    } else {
      this.ensurePartTexture(partId);
    }
  }

  private drawDashedRect(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    w: number,
    h: number,
    color: number,
    alpha: number,
    dash: number,
    gap: number,
  ): void {
    g.lineStyle(2, color, alpha);
    const step = dash + gap;
    for (let i = 0; i < w; i += step) {
      const len = Math.min(dash, w - i);
      g.lineBetween(x + i, y, x + i + len, y);
      g.lineBetween(x + i, y + h, x + i + len, y + h);
    }
    for (let i = 0; i < h; i += step) {
      const len = Math.min(dash, h - i);
      g.lineBetween(x, y + i, x, y + i + len);
      g.lineBetween(x + w, y + i, x + w, y + i + len);
    }
  }

  private drawGrid(): void {
    this.gridGraphics.clear();
    if (!this.editMode && !this.designLayout) return;

    const gridW = this.homeData.gridCols * HOME_CELL_PX;
    const gridH = this.homeData.gridRows * HOME_CELL_PX;
    const lineColor = this.designLayout ? 0x4a8fc9 : 0x3d4f66;
    const lineAlpha = this.designLayout ? 0.82 : 0.55;
    this.gridGraphics.lineStyle(1, lineColor, lineAlpha);

    for (let col = 0; col <= this.homeData.gridCols; col += 1) {
      const x = this.gridOriginX + col * HOME_CELL_PX;
      this.gridGraphics.lineBetween(x, this.gridOriginY, x, this.gridOriginY + gridH);
    }
    for (let row = 0; row <= this.homeData.gridRows; row += 1) {
      const y = this.gridOriginY + row * HOME_CELL_PX;
      this.gridGraphics.lineBetween(this.gridOriginX, y, this.gridOriginX + gridW, y);
    }
  }

  private syncPlacements(): void {
    for (const sprite of this.placementSprites) {
      sprite.destroy();
    }
    this.placementSprites = [];
    this.input.off("drag", this.boundDrag);
    this.input.off("dragend", this.boundDragEnd);
    this.syncMonsterHpMap();

    for (const placement of this.homeData.placements) {
      const part = partById(this.homeData, placement.partId);
      if (!part) continue;
      const useSheet = partHasMonsterSheet(part);
      const key = useSheet ? partSpritesheetTextureKey(part.id) : partTextureKey(part.id);
      if (!this.textures.exists(key)) {
        this.ensurePartTexture(part.id);
        continue;
      }

      const w = part.cellsW * HOME_CELL_PX;
      const h = part.cellsH * HOME_CELL_PX;
      const x = this.gridOriginX + placement.gridX * HOME_CELL_PX + w / 2;
      const y = this.gridOriginY + (placement.gridY + part.cellsH) * HOME_CELL_PX;
      const visualScale = effectivePlacementPlaceScale(part, placement);

      let sprite: PlacementSprite;
      if (useSheet && part.grid) {
        const spr = this.add.sprite(x, y, key, 0);
        fitMonsterSpriteInFootprint(spr, part.grid, part.cellsW, part.cellsH);
        spr.setScale(spr.scale * visualScale);
        const idleKey = partMonsterIdleAnimKey(part.id);
        if (this.anims.exists(idleKey)) spr.play(idleKey);
        sprite = spr as PlacementSprite;
      } else {
        const img = this.add.image(x, y, key) as PlacementSprite;
        fitImageContainInFootprint(img, w, h, 0.5, 1, visualScale);
        sprite = img;
      }
      sprite.placementId = placement.id;
      this.applyPlacementVisual(sprite, placement);
      sprite.setDepth(
        placementDepth(
          normalizePartKind(part.kind),
          placement.gridY,
          part.cellsH,
          effectivePlacementDepthBias(part, placement),
        ),
      );

      if (this.editMode) {
        sprite.setInteractive({ draggable: true, useHandCursor: true });
        sprite.on("pointerdown", () => {
          this.setSelectedPlacement(placement.id);
        });
      } else if (
        normalizePartKind(part.kind) === "attack_dummy" ||
        normalizePartKind(part.kind) === "monster" ||
        (normalizePartKind(part.kind) === "solid" &&
          this.structureCombat?.allowAttack &&
          this.structureCombat.solidPlacementId === placement.id)
      ) {
        sprite.setInteractive({ useHandCursor: true });
        sprite.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
          pointer.event?.stopPropagation();
          this.queueCombatTarget(placement.id);
        });
      }

      if (normalizePartKind(part.kind) === "monster") {
        const hp = this.monsterHp(placement.id);
        sprite.setAlpha(hp > 0 ? 1 : 0.35);
        if (hp <= 0) sprite.disableInteractive();
      }

      this.placementSprites.push(sprite);
    }

    if (this.editMode) {
      this.input.setDraggable(this.placementSprites);
      this.input.on("drag", this.boundDrag);
      this.input.on("dragend", this.boundDragEnd);
    }

    this.drawSelectionOutline();
    this.drawSockets();
    this.drawPlaceGhost();
    if (!this.editMode) {
      this.refreshPlayerDepth();
    }
  }

  private applyPlacementVisual(
    sprite: PlacementSprite,
    placement: Pick<HomePlacement, "flipX" | "flipY" | "rotation">,
  ): void {
    sprite.setFlipX(Boolean(placement.flipX));
    sprite.setFlipY(Boolean(placement.flipY));
    sprite.setAngle(normalizePlacementRotation(placement.rotation));
  }

  private dispatchPlacementTransform(action: HomeAdjustPlacementTransformPayload["action"]): void {
    if (!this.selectedPlacementId) return;
    const detail: HomeAdjustPlacementTransformPayload = {
      placementId: this.selectedPlacementId,
      action,
    };
    window.dispatchEvent(
      new CustomEvent(HomeEvents.AdjustPlacementTransform, { detail }),
    );
  }

  private bindLayerGizmoTransformHit(
    hit: Phaser.GameObjects.Rectangle,
    action: HomeAdjustPlacementTransformPayload["action"],
  ): void {
    hit.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      this.dispatchPlacementTransform(action);
      this.tweens.killTweensOf(this.layerGizmo);
      this.layerGizmo.setScale(1);
      this.tweens.add({
        targets: this.layerGizmo,
        scaleX: 0.94,
        scaleY: 0.94,
        duration: 50,
        yoyo: true,
        ease: "Sine.easeOut",
      });
    });
  }

  private buildLayerGizmo(): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0).setDepth(22);
    const cell = 24;
    const panelW = cell * 3 + 4;
    const panelH = cell * 2 + 6;
    const row1Y = 2 + cell / 2;
    const row2Y = 2 + cell + cell / 2;
    const panel = this.add
      .rectangle(0, 0, panelW, panelH, 0x101820, 0.94)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x6ec8ff, 0.95);
    this.layerGizmoBiasText = this.add
      .text(cell / 2 + 2, row1Y, "0", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "11px",
        fontStyle: "bold",
        color: "#9ed4ff",
      })
      .setOrigin(0.5);
    this.layerGizmoUpFill = this.add
      .rectangle(cell + cell / 2 + 2, row1Y, cell - 2, cell - 2, 0x6ec8ff, 0)
      .setOrigin(0.5);
    this.layerGizmoDownFill = this.add
      .rectangle(cell * 2 + cell / 2 + 2, row1Y, cell - 2, cell - 2, 0x6ec8ff, 0)
      .setOrigin(0.5);
    const upLabel = this.add
      .text(cell + cell / 2 + 2, row1Y, "▲", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px",
        color: "#d8eeff",
      })
      .setOrigin(0.5);
    const downLabel = this.add
      .text(cell * 2 + cell / 2 + 2, row1Y, "▼", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px",
        color: "#d8eeff",
      })
      .setOrigin(0.5);
    const upHit = this.add
      .rectangle(cell + cell / 2 + 2, row1Y, cell, cell, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    const downHit = this.add
      .rectangle(cell * 2 + cell / 2 + 2, row1Y, cell, cell, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    const flipXLabel = this.add
      .text(cell / 2 + 2, row2Y, "↔", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        color: "#d8eeff",
      })
      .setOrigin(0.5);
    const flipYLabel = this.add
      .text(cell + cell / 2 + 2, row2Y, "↕", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        color: "#d8eeff",
      })
      .setOrigin(0.5);
    const rotateLabel = this.add
      .text(cell * 2 + cell / 2 + 2, row2Y, "↻", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        color: "#d8eeff",
      })
      .setOrigin(0.5);
    const flipXHit = this.add
      .rectangle(cell / 2 + 2, row2Y, cell, cell, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    const flipYHit = this.add
      .rectangle(cell + cell / 2 + 2, row2Y, cell, cell, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    const rotateHit = this.add
      .rectangle(cell * 2 + cell / 2 + 2, row2Y, cell, cell, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    const stopPointer = (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
    };
    const bindLayerHit = (hit: Phaser.GameObjects.Rectangle, direction: 1 | -1) => {
      hit.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        stopPointer(pointer);
        this.startLayerGizmoRepeat(direction);
      });
      hit.on("pointerup", (pointer: Phaser.Input.Pointer) => {
        stopPointer(pointer);
        this.stopLayerGizmoRepeat();
      });
      hit.on("pointerout", (pointer: Phaser.Input.Pointer) => {
        if (!pointer.isDown) this.stopLayerGizmoRepeat();
      });
    };
    bindLayerHit(upHit, 1);
    bindLayerHit(downHit, -1);
    this.bindLayerGizmoTransformHit(flipXHit, "flipX");
    this.bindLayerGizmoTransformHit(flipYHit, "flipY");
    this.bindLayerGizmoTransformHit(rotateHit, "rotate");
    container.add([
      panel,
      this.layerGizmoUpFill,
      this.layerGizmoDownFill,
      this.layerGizmoBiasText,
      upLabel,
      downLabel,
      upHit,
      downHit,
      flipXLabel,
      flipYLabel,
      rotateLabel,
      flipXHit,
      flipYHit,
      rotateHit,
    ]);
    container.setVisible(false);
    return container;
  }

  private startLayerGizmoRepeat(direction: 1 | -1): void {
    this.stopLayerGizmoRepeat();
    this.tryAdjustDepthBias(direction);
    this.layerGizmoRepeatTimer = this.time.addEvent({
      delay: 90,
      loop: true,
      callback: () => this.tryAdjustDepthBias(direction),
    });
  }

  private stopLayerGizmoRepeat(): void {
    this.layerGizmoRepeatTimer?.remove();
    this.layerGizmoRepeatTimer = null;
  }

  private tryAdjustDepthBias(direction: 1 | -1): void {
    if (!this.selectedPlacementId) return;
    const placement = this.homeData.placements.find((p) => p.id === this.selectedPlacementId);
    if (!placement) return;
    const part = partById(this.homeData, placement.partId);
    if (!part) return;
    const current = effectivePlacementDepthBias(part, placement);
    const kind = normalizePartKind(part.kind);
    const next = clampPlacementDepthBiasForGrid(
      kind,
      placement.gridY,
      part.cellsH,
      current + direction * HOME_DEPTH_BIAS_STEP,
      HOME_PLACEMENT_DEPTH_BIAS_MIN,
      HOME_PLACEMENT_DEPTH_BIAS_MAX,
    );
    const applied = next !== current;
    this.flashLayerGizmoPress(direction, applied);
    if (!applied) return;
    this.dispatchDepthBiasDelta(direction);
  }

  private flashLayerGizmoPress(direction: 1 | -1, applied: boolean): void {
    const fill = direction > 0 ? this.layerGizmoUpFill : this.layerGizmoDownFill;
    fill.setFillStyle(applied ? 0x6ec8ff : 0xff7070, applied ? 0.55 : 0.4);
    fill.setAlpha(1);
    this.tweens.killTweensOf(fill);
    this.tweens.add({
      targets: fill,
      alpha: 0,
      duration: 160,
      ease: "Cubic.easeOut",
    });
    this.tweens.killTweensOf(this.layerGizmo);
    this.layerGizmo.setScale(1);
    this.tweens.add({
      targets: this.layerGizmo,
      scaleX: 0.94,
      scaleY: 0.94,
      duration: 50,
      yoyo: true,
      ease: "Sine.easeOut",
    });
    if (applied) {
      this.layerGizmoBiasText.setColor("#ffffff");
      this.tweens.add({
        targets: this.layerGizmoBiasText,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 80,
        yoyo: true,
      });
    }
  }

  private dispatchDepthBiasDelta(direction: 1 | -1): void {
    if (!this.selectedPlacementId) return;
    const detail: HomeAdjustPartDepthBiasPayload = {
      placementId: this.selectedPlacementId,
      delta: direction * HOME_DEPTH_BIAS_STEP,
    };
    window.dispatchEvent(
      new CustomEvent(HomeEvents.AdjustPartDepthBias, { detail }),
    );
  }

  private updateLayerGizmo(
    x: number,
    y: number,
    visible: boolean,
    depthBias = 0,
    sortDepth = 20,
  ): void {
    if (!visible) {
      this.layerGizmo.setVisible(false);
      return;
    }
    this.layerGizmo.setPosition(x - 2, y - 62);
    this.layerGizmo.setDepth(sortDepth + 4);
    this.layerGizmoBiasText.setText(String(depthBias));
    this.layerGizmoBiasText.setColor("#9ed4ff");
    this.layerGizmo.setVisible(true);
  }

  private drawSelectionOutline(): void {
    this.selectionOutline.clear();
    if (!this.editMode || !this.selectedPlacementId) {
      this.updateLayerGizmo(0, 0, false);
      return;
    }

    const placement = this.homeData.placements.find((p) => p.id === this.selectedPlacementId);
    if (!placement) {
      this.updateLayerGizmo(0, 0, false);
      return;
    }
    const part = partById(this.homeData, placement.partId);
    if (!part) {
      this.updateLayerGizmo(0, 0, false);
      return;
    }

    const w = part.cellsW * HOME_CELL_PX;
    const h = part.cellsH * HOME_CELL_PX;
    const x = this.gridOriginX + placement.gridX * HOME_CELL_PX;
    const y = this.gridOriginY + placement.gridY * HOME_CELL_PX;

    const depthBias = effectivePlacementDepthBias(part, placement);
    const sortDepth = placementDepth(
      normalizePartKind(part.kind),
      placement.gridY,
      part.cellsH,
      depthBias,
    );
    this.selectionOutline.setDepth(sortDepth + 2);
    this.selectionOutline.lineStyle(2, 0x6ec8ff, 1);
    this.selectionOutline.strokeRect(x, y, w, h);
    this.updateLayerGizmo(x, y, true, depthBias, sortDepth);
  }

  private spriteToGridTopLeftRaw(
    sprite: PlacementSprite,
    part: { cellsW: number; cellsH: number },
  ): { col: number; row: number } {
    const w = part.cellsW * HOME_CELL_PX;
    const h = part.cellsH * HOME_CELL_PX;
    const left = sprite.x - w / 2;
    const top = sprite.y - h;
    const col = Math.round((left - this.gridOriginX) / HOME_CELL_PX);
    const row = Math.round((top - this.gridOriginY) / HOME_CELL_PX);
    return {
      col: Math.max(0, Math.min(col, this.homeData.gridCols - part.cellsW)),
      row: Math.max(0, Math.min(row, this.homeData.gridRows - part.cellsH)),
    };
  }

  private spriteToGridTopLeft(
    sprite: PlacementSprite,
    placementId: string,
  ): { col: number; row: number } | null {
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return null;
    const part = partById(this.homeData, placement.partId);
    if (!part) return null;

    const snap = this.spriteToGridTopLeftRaw(sprite, part);
    if (
      snap.col < 0 ||
      snap.row < 0 ||
      snap.col + part.cellsW > this.homeData.gridCols ||
      snap.row + part.cellsH > this.homeData.gridRows
    ) {
      return null;
    }
    return snap;
  }

  private worldToGrid(worldX: number, worldY: number): { col: number; row: number } | null {
    const localX = worldX - this.gridOriginX;
    const localY = worldY - this.gridOriginY;
    const col = Math.floor(localX / HOME_CELL_PX);
    const row = Math.floor(localY / HOME_CELL_PX);
    if (
      col < 0 ||
      row < 0 ||
      col >= this.homeData.gridCols ||
      row >= this.homeData.gridRows
    ) {
      return null;
    }
    return { col, row };
  }

  private gridCellCenterX(col: number): number {
    return this.gridOriginX + col * HOME_CELL_PX + HOME_CELL_PX / 2;
  }

  private gridCellBottomY(row: number): number {
    return this.gridOriginY + (row + 1) * HOME_CELL_PX;
  }

  private syncDefenderShadowProp(): void {
    const defCol = Math.floor(this.homeData.gridCols / 2);
    const defRow = Math.max(0, Math.floor(this.homeData.gridRows / 2));
    const x = this.gridCellCenterX(defCol);
    const y = this.gridCellBottomY(defRow);
    const depth = playerSortDepth(defRow) - SHADOW_DEPTH_BEHIND;
    if (this.defenderShadowProp?.active) {
      this.defenderShadowProp.setPosition(x, y);
      this.defenderShadowProp.setDepth(depth);
      return;
    }
    this.defenderShadowProp = spawnDefenderShadowProp(this, x, y, depth);
  }

  private refreshPlayerDepth(): void {
    this.player.setDepth(playerSortDepth(this.playerGrid.row));
  }

  private refreshPlayerDepthFromWorldY(worldY: number): void {
    const localY = worldY - this.gridOriginY;
    const row = Math.max(
      0,
      Math.min(this.homeData.gridRows - 1, Math.floor(localY / HOME_CELL_PX)),
    );
    this.player.setDepth(playerSortDepth(row));
  }

  private combatTargetKind(placementId: string): HomePartKind | null {
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return null;
    const part = partById(this.homeData, placement.partId);
    if (!part) return null;
    const kind = normalizePartKind(part.kind);
    if (kind === "attack_dummy" || kind === "monster") return kind;
    if (
      kind === "solid" &&
      this.structureCombat?.allowAttack &&
      this.structureCombat.solidPlacementId === placementId &&
      this.structureHpCurrent > 0 &&
      !this.structureAssaultEnded
    ) {
      return "solid";
    }
    return null;
  }

  private combatTargetFootCell(placementId: string): { col: number; row: number } | null {
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return null;
    const part = partById(this.homeData, placement.partId);
    if (!part || !this.combatTargetKind(placementId)) return null;
    return {
      col: placement.gridX + Math.floor(part.cellsW / 2),
      row: placement.gridY + part.cellsH - 1,
    };
  }

  private manhattanToPlacement(placementId: string): number | null {
    const target = this.combatTargetFootCell(placementId);
    if (!target) return null;
    return (
      Math.abs(target.col - this.playerGrid.col) + Math.abs(target.row - this.playerGrid.row)
    );
  }

  private nearestWalkableAdjacent(
    targetCol: number,
    targetRow: number,
  ): { col: number; row: number } | null {
    const deltas = [
      [0, 1],
      [0, -1],
      [1, 0],
      [-1, 0],
    ];
    const candidates: { col: number; row: number; dist: number }[] = [];
    for (const [dc, dr] of deltas) {
      const col = targetCol + dc;
      const row = targetRow + dr;
      if (!isCellWalkable(this.homeData, col, row)) continue;
      const dist =
        Math.abs(col - this.playerGrid.col) + Math.abs(row - this.playerGrid.row);
      candidates.push({ col, row, dist });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.dist - b.dist);
    return { col: candidates[0].col, row: candidates[0].row };
  }

  private clearCombatStrikeHandler(): void {
    if (this.combatStrikeHandler) {
      this.player.off("animationcomplete", this.combatStrikeHandler);
      this.combatStrikeHandler = null;
    }
  }

  private setCombatActiveBroadcast(active: boolean): void {
    if (this.combatActiveBroadcast === active) return;
    this.combatActiveBroadcast = active;
    window.dispatchEvent(
      new CustomEvent(HomeEvents.CombatActiveChanged, { detail: { active } }),
    );
  }

  private stopCombatLoop(): void {
    this.combatGapTimer?.remove(false);
    this.combatGapTimer = null;
    this.clearCombatStrikeHandler();
    this.combatTargetPlacementId = null;
    this.setCombatActiveBroadcast(false);
    if (this.sitRestEnabled) {
      this.applySitRestPose();
      return;
    }
    if (this.anims.exists("player-idle") && !this.walkTween) {
      this.player.play("player-idle", true);
    }
  }

  private resumeCombatAfterSkill(savedTargetId: string | null): void {
    if (savedTargetId && this.canContinueCombat(savedTargetId)) {
      this.combatTargetPlacementId = savedTargetId;
      this.performCombatStrike(savedTargetId);
      return;
    }
    if (savedTargetId) {
      this.combatTargetPlacementId = null;
    }
    if (this.anims.exists("player-idle") && !this.walkTween) {
      this.player.play("player-idle", true);
    }
  }

  private buffBonusNow(): Partial<StatAllocation> | undefined {
    if (!this.activeBuffBonus) return undefined;
    if (Date.now() > this.activeBuffUntil) return undefined;
    return this.activeBuffBonus;
  }

  private equipmentBonusNow(): Partial<StatAllocation> | undefined {
    return this.registry.get("equipmentStatBonus") as Partial<StatAllocation> | undefined;
  }

  private playerCombatRatings(): CombatRatings {
    const bonus = mergeStatBonuses(this.equipmentBonusNow(), this.buffBonusNow());
    return playerRatings(this.progression, bonus);
  }

  private dummyDefenderRatings(): CombatRatings {
    return {
      level: DUMMY_DEFENDER_LEVEL,
      attack: 0,
      defense: 20,
      dodge: 15,
      hit: 50,
      critChance: 0,
      critMultiplier: 1.5,
      attackSpeed: 0,
      moveSpeed: 0,
      hpRegenPerSec: 0,
      manaMaxBonus: 0,
    };
  }

  private defenderRatingsForPlacement(placementId: string): CombatRatings | null {
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return null;
    const part = partById(this.homeData, placement.partId);
    if (!part) return null;
    const kind = this.combatTargetKind(placementId);
    if (kind === "attack_dummy") return this.dummyDefenderRatings();
    if (kind === "monster") {
      const config = monsterCombatFromPart(part);
      return config?.ratings ?? this.dummyDefenderRatings();
    }
    if (kind === "solid" && this.structureCombat) {
      const base = this.dummyDefenderRatings();
      return { ...base, defense: this.structureCombat.defense };
    }
    return null;
  }

  private emitStructureFocus(damageTaken: number): void {
    if (!this.structureCombat?.solidPlacementId) return;
    const placementId = this.structureCombat.solidPlacementId;
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    const part = placement ? partById(this.homeData, placement.partId) : null;
    const payload: TowerFocusTargetChangedPayload = {
      target: {
        name: part?.name ?? "บ้านป้องกัน",
        maxHp: this.structureCombat.maxHp,
        currentHp: this.structureHpCurrent,
        imageDataUrl: part?.imageDataUrl ?? "",
      },
      damageTaken,
    };
    window.dispatchEvent(
      new CustomEvent(TowerEvents.FocusTargetChanged, { detail: payload }),
    );
  }

  private endStructureAssault(destroyed: boolean): void {
    if (this.structureAssaultEnded) return;
    this.structureAssaultEnded = true;
    window.dispatchEvent(
      new CustomEvent(HomeEvents.StructureAssaultEnded, {
        detail: {
          damageThisRun: this.structureSessionDamage,
          destroyed,
        },
      }),
    );
    this.emitStructureFocus(0);
  }

  private applyStructureDamage(placementId: string, damage: number): void {
    if (!this.structureCombat || this.structureCombat.solidPlacementId !== placementId) return;
    const dealt = Math.max(1, Math.floor(damage));
    this.structureSessionDamage += dealt;
    this.structureHpCurrent = Math.max(0, this.structureHpCurrent - dealt);
    this.shakePlacementSprite(placementId);
    this.emitStructureFocus(dealt);
    if (this.structureHpCurrent <= 0) {
      this.endStructureAssault(true);
      this.stopCombatLoop();
    }
  }

  private monsterMaxHpForPlacement(placementId: string): number {
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return monsterDefaultHp();
    const part = partById(this.homeData, placement.partId);
    if (!part) return monsterDefaultHp();
    const config = monsterCombatFromPart(part);
    return config?.maxHp ?? monsterDefaultHp();
  }

  private floaterAnchorForPlacement(placementId: string): { x: number; y: number } | null {
    const sprite = this.placementSprites.find((s) => s.placementId === placementId);
    if (!sprite) return null;
    return { x: sprite.x, y: sprite.y - 20 };
  }

  private flashPlayerCrit(): void {
    this.player.setTint(CRIT_TINT_GOLD);
    this.time.delayedCall(140, () => {
      if (this.player.active) this.player.clearTint();
    });
  }

  private monsterPlacementsInAoeAroundPlayer(rangeMultiplier = 1): string[] {
    const pc = this.playerGrid;
    const radius = 1 * rangeMultiplier;
    const hits: string[] = [];
    for (const placement of this.homeData.placements) {
      if (this.combatTargetKind(placement.id) !== "monster") continue;
      if (this.monsterHp(placement.id) <= 0) continue;
      const foot = this.combatTargetFootCell(placement.id);
      if (!foot) continue;
      const dc = Math.abs(foot.col - pc.col);
      const dr = Math.abs(foot.row - pc.row);
      if (dc <= radius && dr <= radius) hits.push(placement.id);
    }
    return hits;
  }

  private resolveStrikeOnTarget(placementId: string, damageMultiplier = 1): void {
    const defender = this.defenderRatingsForPlacement(placementId);
    if (!defender) return;
    const attacker = this.playerCombatRatings();
    const result = resolveHit(attacker, defender, damageMultiplier);
    const anchor = this.floaterAnchorForPlacement(placementId);
    if (anchor && this.combatFloaters) {
      if (result.kind === "miss") {
        this.combatFloaters.spawn(anchor.x, anchor.y, "miss");
      } else {
        this.combatFloaters.spawn(
          anchor.x,
          anchor.y,
          result.crit ? "crit" : "hit",
          result.damage,
        );
      }
    }
    if (result.crit) {
      this.flashPlayerCrit();
      if (this.skillFxSprite?.active) {
        this.skillFxSprite.setTint(CRIT_TINT_GOLD);
      }
    }
    const kind = this.combatTargetKind(placementId);
    if (kind === "monster" && result.kind === "hit") {
      this.applyMonsterDamage(placementId, result.damage);
    } else if (kind === "attack_dummy" && result.kind === "hit") {
      this.shakePlacementSprite(placementId);
    } else if (kind === "solid" && result.kind === "hit") {
      this.applyStructureDamage(placementId, result.damage);
    }
  }

  private shakePlacementSprite(placementId: string): void {
    const sprite = this.placementSprites.find((s) => s.placementId === placementId);
    if (!sprite) return;
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    const part = placement ? partById(this.homeData, placement.partId) : null;
    if (sprite instanceof Phaser.GameObjects.Sprite && part && partHasMonsterSheet(part)) {
      const attackKey = partMonsterAttackAnimKey(part.id);
      const idleKey = partMonsterIdleAnimKey(part.id);
      if (this.anims.exists(attackKey)) {
        sprite.play(attackKey);
        const onAttackDone = (animation: Phaser.Animations.Animation) => {
          if (animation.key !== attackKey) return;
          sprite.off("animationcomplete", onAttackDone);
          if (sprite.active && this.anims.exists(idleKey)) sprite.play(idleKey);
        };
        sprite.on("animationcomplete", onAttackDone);
      }
    }
    this.tweens.add({
      targets: sprite,
      x: sprite.x + 5,
      duration: 45,
      yoyo: true,
      repeat: 2,
      ease: "Sine.easeOut",
    });
  }

  private monsterHp(placementId: string): number {
    const now = Date.now();
    const last = this.monsterLastHitAtByPlacementId.get(placementId) ?? 0;
    const max = this.monsterMaxHpForPlacement(placementId);
    if (!this.monsterHpByPlacementId.has(placementId)) {
      this.monsterHpByPlacementId.set(placementId, max);
    } else if (now - last > MONSTER_HP_IDLE_RESET_MS) {
      this.monsterHpByPlacementId.set(placementId, max);
      this.restoreMonsterPlacement(placementId);
    }
    return this.monsterHpByPlacementId.get(placementId) ?? max;
  }

  private restoreMonsterPlacement(placementId: string): void {
    const sprite = this.placementSprites.find((s) => s.placementId === placementId);
    if (!sprite) return;
    sprite.setAlpha(1);
    sprite.setInteractive({ useHandCursor: true });
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    const part = placement ? partById(this.homeData, placement.partId) : null;
    if (sprite instanceof Phaser.GameObjects.Sprite && part && partHasMonsterSheet(part)) {
      const idleKey = partMonsterIdleAnimKey(part.id);
      if (this.anims.exists(idleKey)) sprite.play(idleKey);
    }
  }

  private syncMonsterHpMap(): void {
    const alive = new Set<string>();
    for (const placement of this.homeData.placements) {
      const kind = this.combatTargetKind(placement.id);
      if (kind !== "monster") continue;
      alive.add(placement.id);
      this.monsterHp(placement.id);
    }
    for (const id of this.monsterHpByPlacementId.keys()) {
      if (!alive.has(id)) {
        this.monsterHpByPlacementId.delete(id);
        this.monsterLastHitAtByPlacementId.delete(id);
      }
    }
  }

  private canContinueCombat(placementId: string): boolean {
    const kind = this.combatTargetKind(placementId);
    if (!kind) return false;
    const dist = this.manhattanToPlacement(placementId);
    if (dist === null || dist > combatAttackRange()) return false;
    if (kind === "attack_dummy") return true;
    return this.monsterHp(placementId) > 0;
  }

  private applyMonsterDamage(placementId: string, damage: number): boolean {
    this.monsterLastHitAtByPlacementId.set(placementId, Date.now());
    const hp = this.monsterHp(placementId) - Math.max(1, damage);
    this.monsterHpByPlacementId.set(placementId, Math.max(0, hp));
    this.shakePlacementSprite(placementId);
    const sprite = this.placementSprites.find((s) => s.placementId === placementId);
    if (sprite) {
      if (hp <= 0) {
        sprite.setAlpha(0.35);
        sprite.disableInteractive();
        const placement = this.homeData.placements.find((p) => p.id === placementId);
        const part = placement ? partById(this.homeData, placement.partId) : null;
        const config = part ? monsterCombatFromPart(part) : null;
        const xpReward = config?.xpReward ?? 0;
        if (xpReward > 0) {
          window.dispatchEvent(
            new CustomEvent(HomeEvents.MonsterDefeated, {
              detail: { placementId, xpReward },
            }),
          );
        }
      }
    }
    return hp > 0;
  }

  private faceCombatTarget(placementId: string): void {
    const placement = this.homeData.placements.find((p) => p.id === placementId);
    if (!placement) return;
    const part = partById(this.homeData, placement.partId);
    if (!part) return;
    const w = part.cellsW * HOME_CELL_PX;
    const targetX = this.gridOriginX + placement.gridX * HOME_CELL_PX + w / 2;
    this.player.setFlipX(targetX > this.player.x);
    this.refreshPlayerDepth();
  }

  private performCombatStrike(placementId: string): void {
    if (!this.canContinueCombat(placementId)) {
      this.stopCombatLoop();
      return;
    }

    const kind = this.combatTargetKind(placementId);
    if (!kind) {
      this.stopCombatLoop();
      return;
    }

    this.clearWalkPath();
    this.faceCombatTarget(placementId);

    const onStrikeEnd = () => {
      if (this.combatTargetPlacementId !== placementId) return;
      this.resolveStrikeOnTarget(placementId, 1);
      if (kind === "monster") {
        const alive = this.monsterHp(placementId) > 0;
        if (!alive) {
          this.stopCombatLoop();
          return;
        }
      }
      if (kind === "solid" && this.structureHpCurrent <= 0) {
        this.stopCombatLoop();
        return;
      }
      if (this.canContinueCombat(placementId)) {
        const ratings = this.playerCombatRatings();
        const gap = attackGapMs(ratings.level, ratings.attackSpeed);
        this.combatGapTimer?.remove(false);
        this.combatGapTimer = this.time.delayedCall(gap, () => {
          if (this.combatTargetPlacementId === placementId) {
            this.performCombatStrike(placementId);
          }
        });
        return;
      }
      this.stopCombatLoop();
    };

    if (this.anims.exists("player-attack")) {
      this.clearCombatStrikeHandler();
      this.player.play("player-attack");
      this.combatStrikeHandler = (animation: Phaser.Animations.Animation) => {
        if (animation.key !== "player-attack") return;
        this.clearCombatStrikeHandler();
        onStrikeEnd();
      };
      this.player.on("animationcomplete", this.combatStrikeHandler);
      return;
    }

    onStrikeEnd();
  }

  private startCombatLoop(placementId: string): void {
    if (!this.combatTargetKind(placementId)) return;
    if (this.combatTargetKind(placementId) === "monster" && this.monsterHp(placementId) <= 0) {
      return;
    }
    if (this.combatTargetKind(placementId) === "solid") {
      this.emitStructureFocus(0);
    }
    this.combatTargetPlacementId = placementId;
    this.setCombatActiveBroadcast(true);
    this.performCombatStrike(placementId);
  }

  private tryEngageCombatTarget(placementId: string): void {
    if (!this.combatTargetKind(placementId)) return;
    const dist = this.manhattanToPlacement(placementId);
    if (dist === null) return;
    if (dist <= combatAttackRange()) {
      this.startCombatLoop(placementId);
      return;
    }
    const target = this.combatTargetFootCell(placementId);
    if (!target) return;
    const step = this.nearestWalkableAdjacent(target.col, target.row);
    if (!step) return;
    this.pendingAttackPlacementId = placementId;
    this.movePlayerTo(this.gridCellCenterX(step.col), this.gridCellBottomY(step.row));
  }

  private queueCombatTarget(placementId: string): void {
    if (this.editMode || this.sitRestEnabled) return;
    this.tryEngageCombatTarget(placementId);
  }

  private clearWalkPath(): void {
    this.walkPathQueue = [];
    this.walkTween?.stop();
    this.walkTween = null;
  }

  private finishWalkStep(): void {
    if (this.anims.exists("player-idle")) {
      this.player.play("player-idle", true);
    }
    if (this.walkPathQueue.length > 0) {
      this.advanceWalkPath();
      return;
    }
    const pending = this.pendingAttackPlacementId;
    if (pending) {
      this.pendingAttackPlacementId = null;
      this.tryEngageCombatTarget(pending);
    }
  }

  private advanceWalkPath(): void {
    const next = this.walkPathQueue.shift();
    if (!next) {
      this.finishWalkStep();
      return;
    }
    if (!isCellWalkable(this.homeData, next.col, next.row)) {
      this.walkPathQueue = [];
      this.finishWalkStep();
      return;
    }
    this.tweenPlayerToGrid(next, { hop: false });
  }

  private tweenPlayerToGrid(
    grid: { col: number; row: number },
    opts: { hop: boolean; hopManhattan?: number },
  ): void {
    const targetX = this.gridCellCenterX(grid.col);
    const targetY = this.gridCellBottomY(grid.row);
    const distance = Phaser.Math.Distance.Between(
      this.player.x,
      this.player.y,
      targetX,
      targetY,
    );

    this.walkTween?.stop();
    // Art faces left by default — mirror when moving right (same as TowerScene).
    this.player.setFlipX(targetX > this.player.x);

    if (this.anims.exists("player-run")) {
      this.player.play("player-run", true);
    }

    const finish = () => {
      this.player.x = targetX;
      this.player.y = targetY;
      this.playerGrid = grid;
      this.walkTween = null;
      this.refreshPlayerDepth();
      this.finishWalkStep();
    };

    if (opts.hop && opts.hopManhattan && opts.hopManhattan > 1) {
      const startX = this.player.x;
      const startY = this.player.y;
      const manhattan = opts.hopManhattan;
      const peakY = Math.min(startY, targetY) - 28 - manhattan * 6;
      const duration = Math.max(240, distance * 5);
      this.walkTween = this.tweens.add({
        targets: this.player,
        duration,
        ease: "Linear",
        onUpdate: (tween) => {
          const t = tween.progress;
          this.player.x = startX + (targetX - startX) * t;
          this.player.y =
            (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * peakY + t * t * targetY;
          this.refreshPlayerDepthFromWorldY(this.player.y);
        },
        onComplete: finish,
      });
      return;
    }

    const moveMult = moveSpeedMultiplier(this.playerCombatRatings().moveSpeed);
    const duration = Math.max(120, (distance * 7) / moveMult);
    this.walkTween = this.tweens.add({
      targets: this.player,
      x: targetX,
      y: targetY,
      duration,
      ease: "Sine.easeInOut",
      onUpdate: () => {
        this.refreshPlayerDepthFromWorldY(this.player.y);
      },
      onComplete: finish,
    });
  }

  private movePlayerTo(worldX: number, worldY: number): void {
    if (this.sitRestEnabled) {
      emitCombatSitRestChanged(false);
    }
    const grid = this.worldToGrid(worldX, worldY);
    if (!grid) return;
    if (!isCellWalkable(this.homeData, grid.col, grid.row)) return;

    const manhattan =
      Math.abs(grid.col - this.playerGrid.col) + Math.abs(grid.row - this.playerGrid.row);
    const ontoPlatform = cellHasPlatform(this.homeData, grid.col, grid.row);
    const fromPlatform = cellHasPlatform(
      this.homeData,
      this.playerGrid.col,
      this.playerGrid.row,
    );
    const canHop =
      (ontoPlatform || fromPlatform) &&
      manhattan > 1 &&
      manhattan <= PLATFORM_JUMP_RANGE;

    if (ontoPlatform && !fromPlatform && manhattan > PLATFORM_JUMP_RANGE) {
      return;
    }

    const targetX = this.gridCellCenterX(grid.col);
    const targetY = this.gridCellBottomY(grid.row);
    const distance = Phaser.Math.Distance.Between(
      this.player.x,
      this.player.y,
      targetX,
      targetY,
    );
    if (manhattan === 0 && distance < 6) return;

    this.clearWalkPath();

    if (canHop) {
      this.tweenPlayerToGrid(grid, { hop: true, hopManhattan: manhattan });
      return;
    }

    const path = findWalkPath(
      this.homeData,
      this.playerGrid.col,
      this.playerGrid.row,
      grid.col,
      grid.row,
    );
    if (path === null) return;
    if (path.length === 0) return;

    this.walkPathQueue = path;
    this.advanceWalkPath();
  }
}
